/**
 * Backup utilities for video editing app
 */

// localStorage keys for backup
export const BACKUP_KEY = 'beevids-parts-backup';
export const GROUPS_BACKUP_KEY = 'beevids-groups-backup';

/**
 * Backup parts and groups to localStorage
 */
export const backupPartsAndGroups = (projectId, parts, groups, individualPartsLang = null) => {
  try {
    // Always allow backup, even for empty data (for recovery purposes)
    const backup = {
      timestamp: new Date().toISOString(),
      projectId,
      parts: Array.isArray(parts) ? parts : [],
      groups: Array.isArray(groups) ? groups : [],
      individualPartsLanguage: individualPartsLang || null
    };
    
    // Validate backup data before saving
    if (!projectId) {
      console.warn('💾 Skip backup - no project ID');
      return;
    }
    
    const existingBackups = JSON.parse(localStorage.getItem(BACKUP_KEY) || '{}');
    existingBackups[projectId] = backup;
    
    // Try to save with error handling
    try {
      localStorage.setItem(BACKUP_KEY, JSON.stringify(existingBackups));
      console.log('💾 Backed up parts and groups:', {
        projectId,
        partsCount: backup.parts.length,
        groupsCount: backup.groups.length,
        hasIndividualLang: !!backup.individualPartsLanguage,
        isEmpty: backup.parts.length === 0 && backup.groups.length === 0
      });
    } catch (storageError) {
      console.error('💾 localStorage write failed:', storageError);
      // Don't throw error, just log it
    }
  } catch (error) {
    console.error('❌ Failed to backup parts and groups:', error);
    // Don't throw error to avoid disrupting main flow
  }
};

/**
 * Restore parts and groups from backup
 */
export const restorePartsAndGroups = (projectId) => {
  try {
    const backups = JSON.parse(localStorage.getItem(BACKUP_KEY) || '{}');
    const backup = backups[projectId];
    
    console.log('🔍 Checking backup for project:', projectId, {
      backupExists: !!backup,
      backupData: backup ? {
        parts: backup.parts?.length || 0,
        groups: backup.groups?.length || 0,
        hasLang: !!backup.individualPartsLanguage,
        timestamp: backup.timestamp
      } : null
    });
    
    if (backup) {
      const hasRealData = (backup.parts && backup.parts.length > 0) || 
                         (backup.groups && backup.groups.length > 0) || 
                         backup.individualPartsLanguage;
      
      if (hasRealData) {
        console.log('🔒 DISABLED: Backup restore disabled to prevent subtitle preset conflicts');
        console.log('🔄 Would have restored backup from:', backup.timestamp, {
          parts: backup.parts?.length || 0,
          groups: backup.groups?.length || 0,
          hasLang: !!backup.individualPartsLanguage
        });
        // 🔒 RETURN EMPTY DATA INSTEAD OF RESTORED DATA
        return { parts: [], groups: [], individualPartsLanguage: null };
      } else {
        console.log('💾 Removing empty backup for project:', projectId);
        // Remove empty backup
        delete backups[projectId];
        localStorage.setItem(BACKUP_KEY, JSON.stringify(backups));
        return { parts: [], groups: [], individualPartsLanguage: null };
      }
    }
  } catch (error) {
    console.error('❌ Failed to restore backup:', error);
  }
  
  return { parts: [], groups: [], individualPartsLanguage: null };
};

/**
 * Clear all backups for a project
 */
export const clearBackupsForProject = (projectId) => {
  try {
    if (!projectId) {
      console.warn('🗑️ Cannot clear backups - no project ID');
      return false;
    }
    
    const backups = JSON.parse(localStorage.getItem(BACKUP_KEY) || '{}');
    const hadBackup = !!backups[projectId];
    delete backups[projectId];
    localStorage.setItem(BACKUP_KEY, JSON.stringify(backups));
    
    console.log('🗑️ Cleared backups for project:', { projectId, hadBackup });
    return true;
  } catch (error) {
    console.error('❌ Failed to clear backups:', error);
    return false;
  }
};

/**
 * Force immediate backup sync
 */
export const forceBackupSync = (projectId, parts, groups, individualPartsLang = null) => {
  console.log('🔄 Force backup sync requested:', { projectId, parts: parts?.length, groups: groups?.length });
  backupPartsAndGroups(projectId, parts, groups, individualPartsLang);
};

/**
 * Debug all backups (for development)
 */
export const debugAllBackups = () => {
  try {
    const backups = JSON.parse(localStorage.getItem(BACKUP_KEY) || '{}');
    console.log('🐛 All project backups:', backups);
    
    Object.entries(backups).forEach(([projectId, backup]) => {
      console.log(`🔍 Project ${projectId}:`, {
        timestamp: backup.timestamp,
        parts: backup.parts?.length || 0,
        groups: backup.groups?.length || 0,
        hasLang: !!backup.individualPartsLanguage
      });
    });
    
    return backups;
  } catch (error) {
    console.error('❌ Failed to debug backups:', error);
    return {};
  }
}; 